import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import type { StockControlRow } from '@econtractor/types';
import * as apiWarehouse from 'api/apiWarehouses';
import { useAuth } from 'context/AuthContext';
import { useGlobal } from 'context/GlobalContext';
import { useToast } from 'context/ToastContext';
import { useListOnPage } from 'utils/helpers/list/useListOnPage';
import { useListRefreshEffect } from 'utils/helpers/list/useListRefreshEffect';
import { useRegistryColumns } from 'utils/helpers/list/useRegistryColumns';
import {
    defaultStockControlFilter,
    isDefaultStockControlFilter,
    loadStockControlFilter,
    saveStockControlFilter,
    type StockControlFilter,
} from './stockControlFiltersStorage';

export type StockControlColumnId = 'category' | 'article';

const STOCK_CONTROL_COLUMN_DEFS: { id: StockControlColumnId; defaultVisible: boolean }[] = [
    { id: 'category', defaultVisible: false },
    { id: 'article', defaultVisible: false },
];

type ListResponse = {
    rows?: StockControlRow[];
    paginator?: {
        countRows: number;
        countPages: number;
        currentPage: number;
        onPage: number;
    };
} | null;

export const useStockControlPage = () => {
    const { t } = useTranslation(['Warehouses', 'common']);
    const { g } = useGlobal();
    const { auth } = useAuth();
    const { showToast } = useToast();
    const location = useLocation();
    const onPage = useListOnPage();
    const userId = Number(auth?.user?.ID || 0);

    const [rows, setRows] = useState<StockControlRow[] | null>(null);
    const [page, setPage] = useState<number | null>(null);
    const [showSpinner, setShowSpinner] = useState(false);
    const [order, setOrder] = useState('prod_name');
    const [desc, setDesc] = useState(false);
    const [filter, setFilter] = useState<StockControlFilter>(defaultStockControlFilter);
    const [showFilter, setShowFilter] = useState(false);
    const [prevFilter, setPrevFilter] = useState<StockControlFilter>(defaultStockControlFilter);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const { visible: listColumns, toggle: toggleListColumn } = useRegistryColumns(
        'registryColumns:stockControl',
        STOCK_CONTROL_COLUMN_DEFS,
    );

    const prevPayload = useRef<string | null>(null);
    const skipSaveRef = useRef(true);

    useEffect(() => {
        if (!userId) return;
        skipSaveRef.current = true;
        setFilter(loadStockControlFilter(userId));
    }, [userId]);

    useEffect(() => {
        if (!userId) return;
        if (skipSaveRef.current) {
            skipSaveRef.current = false;
            return;
        }
        saveStockControlFilter(userId, filter);
    }, [filter, userId]);

    const applyResponse = useCallback((res: NonNullable<ListResponse>) => {
        setRows(res.rows ?? []);
        g.setPaginator((prev: Record<string, unknown>) => ({
            ...prev,
            [location.pathname]: res.paginator,
        }));
    }, [g, location.pathname]);

    const requestParams = useCallback((
        nextPage: number,
        nextFilter: StockControlFilter,
        nextOrder: string,
        nextDesc: boolean,
    ) => {
        const search = `%${g?.search?.[location.pathname] || ''}%`;
        return {
            search,
            page: nextPage < 0 ? 1 : nextPage || 1,
            onPage,
            order: `${nextOrder ? `order by ${nextOrder} ` : ''}${nextDesc ? 'DESC' : ''}`,
            includeMain: nextFilter.includeMain,
            includeCustom: nextFilter.includeCustom,
            includeTeam: nextFilter.includeTeam,
            includeReserve: nextFilter.includeReserve,
            categoryIds: nextFilter.categoryIds.length ? nextFilter.categoryIds : undefined,
        };
    }, [g, location.pathname, onPage]);

    const fetchList = async (force = false) => {
        if (!page) return null;
        if (force) prevPayload.current = null;
        const params = requestParams(page, filter, order, desc);
        const key = JSON.stringify(params);
        if (prevPayload.current === key) return null;
        prevPayload.current = key;
        setShowSpinner(true);
        const res = await apiWarehouse.getStockControlList(params) as ListResponse;
        setShowSpinner(false);
        if (!res) return showToast(t('common:messError'), 'danger');
        applyResponse(res);
        return res;
    };

    const resetList = useCallback(async () => {
        const nextFilter = defaultStockControlFilter();
        const params = requestParams(1, nextFilter, 'prod_name', false);
        prevPayload.current = JSON.stringify(params);
        setOrder('prod_name');
        setDesc(false);
        setFilter(nextFilter);
        setPrevFilter(nextFilter);
        g.setPaginatorPage(location.pathname, 1);
        setPage(1);
        setShowSpinner(true);
        const res = await apiWarehouse.getStockControlList(params) as ListResponse;
        setShowSpinner(false);
        if (!res) {
            prevPayload.current = null;
            return showToast(t('common:messError'), 'danger');
        }
        applyResponse(res);
    }, [applyResponse, g, location.pathname, requestParams, showToast, t]);

    useListRefreshEffect(listResetNonce, () => {
        void resetList();
    });

    useListRefreshEffect(listRefreshNonce, () => {
        prevPayload.current = null;
        void fetchList(true);
    });

    useEffect(() => {
        if (page === 1) void fetchList();
        else setPage(1);
    }, [g.search?.[location.pathname], g.target, filter, order, desc]);

    useEffect(() => {
        const nextPage = g?.paginator?.[location.pathname]?.currentPage || 1;
        if (page !== nextPage) setPage(nextPage);
    }, [
        g?.paginator?.[location.pathname]?.currentPage,
        g?.paginator?.[location.pathname]?.countRows,
        g?.paginator?.[location.pathname]?.onPage,
    ]);

    useEffect(() => {
        void fetchList();
    }, [page, onPage]);

    const handleShowFilter = (show: boolean) => {
        if (show) setPrevFilter(filter);
        setShowFilter(show);
    };

    return {
        t,
        rows,
        showSpinner,
        order,
        setOrder,
        desc,
        setDesc,
        filter,
        setFilter,
        showFilter,
        handleShowFilter,
        prevFilter,
        setPrevFilter,
        filterActive: !isDefaultStockControlFilter(filter),
        fetchList,
        triggerListReset: () => setListResetNonce((nonce) => nonce + 1),
        triggerListRefresh: () => setListRefreshNonce((nonce) => nonce + 1),
        showCategory: Boolean(listColumns.category),
        showArticle: Boolean(listColumns.article),
        toggleListColumn,
    };
};
